import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON, postFormData } from '@/lib/api'
import type { CustomFieldsResponse } from '@/lib/contacts/types'
import {
  importFormData,
  importMappingOptions,
  initialMappings,
  mutationMessage,
  parseCsvColumns,
  readFileText,
} from '@/pages/contact-import-wizard/helpers'
import {
  MappingStep,
  PreviewStep,
  ResultsStep,
  Stepper,
  UploadStep,
} from '@/pages/contact-import-wizard/ContactImportWizardSteps'
import type {
  ImportCommitResponse,
  ImportKind,
  ImportPreviewResponse,
  ImportStep,
  ImportSummary,
  UploadState,
} from '@/pages/contact-import-wizard/types'

export function ContactImportWizardPage() {
  const [step, setStep] = React.useState<ImportStep>('upload')
  const [kind, setKind] = React.useState<ImportKind>('People')
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [upload, setUpload] = React.useState<UploadState | null>(null)
  const [fileMessage, setFileMessage] = React.useState<string | null>(null)
  const [preview, setPreview] = React.useState<ImportSummary | null>(null)
  const [result, setResult] = React.useState<ImportSummary | null>(null)
  const customFieldsQuery = useQuery({
    queryKey: ['custom-fields'],
    queryFn: () => getJSON<CustomFieldsResponse>('/api/v1/custom_fields'),
    retry: false,
  })
  const customFields = customFieldsQuery.data?.custom_fields ?? []
  const previewImport = useMutation({
    mutationFn: () =>
      postFormData<ImportPreviewResponse>(
        '/api/v1/contacts/imports/people/preview',
        importFormData({ upload, kind }),
      ),
    onSuccess: (response) => {
      setPreview(response.people_import_preview)
      setResult(null)
      setFileMessage(null)
      setStep('preview')
    },
  })
  const commitImport = useMutation({
    mutationFn: () =>
      postFormData<ImportCommitResponse>(
        '/api/v1/contacts/imports/people/commit',
        importFormData({ upload, kind }),
      ),
    onSuccess: (response) => {
      setResult(response.people_import_commit)
      setStep('results')
    },
  })

  async function startMapping() {
    if (!selectedFile) return

    const columns = parseCsvColumns(await readFileText(selectedFile))
    if (columns.length === 0) {
      setFileMessage('Choose a CSV with a header row.')
      return
    }

    setUpload({ file: selectedFile, columns, mappings: initialMappings(columns, customFields) })
    setPreview(null)
    setResult(null)
    setFileMessage(null)
    previewImport.reset()
    commitImport.reset()
    setStep('mapping')
  }

  function updateMapping(header: string, field: string) {
    if (!upload) return

    setUpload({ ...upload, mappings: { ...upload.mappings, [header]: field } })
  }

  function resetWizard() {
    setSelectedFile(null)
    setUpload(null)
    setPreview(null)
    setResult(null)
    setFileMessage(null)
    setStep('upload')
    previewImport.reset()
    commitImport.reset()
  }

  if (customFieldsQuery.isLoading) {
    return <EmptyState title="Loading import options" className="min-h-full" />
  }

  if (customFieldsQuery.isError) {
    return (
      <EmptyState
        title="Import options unavailable"
        description="Refresh and try again."
        className="min-h-full"
      />
    )
  }

  return (
    <div data-testid="contact-import-wizard" className="w-full max-w-2xl px-6 py-6">
      <header className="space-y-1">
        <h1 className="text-heading-sm font-normal text-foreground">Import contacts</h1>
      </header>

      <div className="space-y-5 pt-5">
        <Stepper current={step} />

        <WizardStepContent
          step={step}
          kind={kind}
          file={selectedFile}
          fileMessage={fileMessage}
          upload={upload}
          customFields={customFields}
          preview={preview}
          result={result}
          previewPending={previewImport.isPending}
          previewError={mutationMessage(previewImport.error)}
          commitPending={commitImport.isPending}
          commitError={mutationMessage(commitImport.error)}
          onKindChange={setKind}
          onFileChange={(file) => {
            setSelectedFile(file)
            setFileMessage(null)
          }}
          onContinue={() => void startMapping()}
          onBackToUpload={() => setStep('upload')}
          onBackToMapping={() => setStep('mapping')}
          onMappingChange={updateMapping}
          onPreview={() => previewImport.mutate()}
          onCommit={() => commitImport.mutate()}
          onDone={resetWizard}
        />
      </div>
    </div>
  )
}

function WizardStepContent({
  step,
  kind,
  file,
  fileMessage,
  upload,
  customFields,
  preview,
  result,
  previewPending,
  previewError,
  commitPending,
  commitError,
  onKindChange,
  onFileChange,
  onContinue,
  onBackToUpload,
  onBackToMapping,
  onMappingChange,
  onPreview,
  onCommit,
  onDone,
}: {
  step: ImportStep
  kind: ImportKind
  file: File | null
  fileMessage: string | null
  upload: UploadState | null
  customFields: CustomFieldsResponse['custom_fields']
  preview: ImportSummary | null
  result: ImportSummary | null
  previewPending: boolean
  previewError: string | null
  commitPending: boolean
  commitError: string | null
  onKindChange: (kind: ImportKind) => void
  onFileChange: (file: File | null) => void
  onContinue: () => void
  onBackToUpload: () => void
  onBackToMapping: () => void
  onMappingChange: (header: string, field: string) => void
  onPreview: () => void
  onCommit: () => void
  onDone: () => void
}) {
  return (
    <>
      {step === 'upload' && (
        <UploadStep
          kind={kind}
          file={file}
          message={fileMessage}
          onKindChange={onKindChange}
          onFileChange={onFileChange}
          onContinue={onContinue}
        />
      )}
      {step === 'mapping' && upload && (
        <MappingStep
          upload={upload}
          options={importMappingOptions(customFields)}
          pending={previewPending}
          error={previewError}
          onBack={onBackToUpload}
          onMappingChange={onMappingChange}
          onPreview={onPreview}
        />
      )}
      {step === 'preview' && preview && (
        <PreviewStep
          summary={preview}
          pending={commitPending}
          error={commitError}
          onBack={onBackToMapping}
          onCommit={onCommit}
        />
      )}
      {step === 'results' && result && <ResultsStep summary={result} onDone={onDone} />}
    </>
  )
}
